<template>
    <!-- Static sidebar for desktop -->
    <div class="hidden xl:flex xl:flex-shrink-0">
        <div class="flex flex-col w-64">
            <!-- Sidebar component, swap this element with another sidebar if you like -->
            <div class="flex-1 flex flex-col min-h-0"
                :style="{ backgroundColor: $page.props.branding?.sidebar_color || '#2f3441', color: $page.props.branding?.sidebar_text_color || '#ffffff' }">
                <div class="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
                    <nav class="flex-1" aria-label="Sidebar">
                        <div class="px-2 space-y-1">
                            <!-- Overview Topic -->
                            <Link v-if="overviewTopic" :key="overviewTopic.name" :href="makeRoute(overviewTopic)"
                                :class="[
                                    isCurrent(overviewTopic)
                                        ? 'bg-gray-200 text-gray-900'
                                        : 'hover:bg-gray-50 hover:text-gray-900',
                                    'group flex items-center px-2 py-2 text-sm rounded-md',
                                ]">
                            <i :class="isCurrent(overviewTopic)
                                    ? [
                                        'text-gray-500 mr-3 h-6 w-6',
                                        overviewTopic.icon,
                                    ]
                                    : [
                                        'group-hover:text-gray-500 mr-3 h-6 w-6',
                                        overviewTopic.icon,
                                    ]
                                " aria-hidden="true">
                                <span class="relative text-sm">
                                    <ClockIcon class="absolute -mt-2 -ml-1 h-3 w-3 text-red-400"
                                        v-if="overviewTopic.overdue" aria-hidden="true" />
                                    <CheckCircleIcon class="absolute -mt-2 -ml-1 h-3 w-3 text-green-400" v-if="
                                        overviewTopic.state === 'completed' &&
                                        !overviewTopic.overdue
                                    " aria-hidden="true" />
                                    <PlayIcon class="absolute -mt-2 -ml-1 h-3 w-3 text-yellow-400" v-if="
                                        overviewTopic.state === 'in_process' &&
                                        !overviewTopic.overdue
                                    " aria-hidden="true" />
                                    <ExclamationCircleIcon class="absolute -mt-2 -ml-1 h-3 w-3 text-red-400" v-if="
                                        overviewTopic.state === 'blocked' &&
                                        !overviewTopic.overdue
                                    " aria-hidden="true" />
                                </span>
                            </i>
                            {{ overviewTopic.name }}
                            </Link>

                            <!-- Communication Section -->
                            <div v-if="canShowCommunication || canShowNotes" class="!mt-5 mb-6 space-y-1">
                                <p class="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                                    {{ $t('apartments.communication') }}
                                </p>

                                <Link v-if="canShowNotes" :href="route(notesRouteString, apartment.uuid)" :class="[
                                    isNotesActive
                                        ? 'bg-gray-200 text-gray-900'
                                        : 'hover:bg-gray-50 hover:text-gray-900',
                                    'group flex items-center px-3 py-2 text-sm font-medium rounded-md',
                                ]">
                                <PencilAltIcon :class="[
                                    isNotesActive ? 'text-gray-500' : 'text-gray-400 group-hover:text-gray-500',
                                    'mr-3 h-6 w-6'
                                ]" aria-hidden="true" />
                                <span class="flex-1">{{ $t("apartments.notes") }}</span>
                                </Link>

                                <Link v-if="canShowCommunication" :href="route(chatRouteString, defaultChatRouteParams)" :class="[
                                    isChatActive
                                        ? 'bg-gray-200 text-gray-900'
                                        : 'hover:bg-gray-50 hover:text-gray-900',
                                    'group flex items-center px-3 py-2 text-sm font-medium rounded-md',
                                ]">
                                <ChatIcon :class="[
                                    isChatActive ? 'text-gray-500' : 'text-gray-400 group-hover:text-gray-500',
                                    'mr-3 h-6 w-6'
                                ]" aria-hidden="true" />
                                <span class="flex-1">{{ $t('chat.chat') || 'Chat' }}</span>
                                <span v-if="totalUnreadCount > 0"
                                    class="ml-3 inline-block py-0.5 px-3 text-xs font-medium bg-red-500 text-white rounded-full">
                                    {{ totalUnreadCount }}
                                </span>
                                </Link>
                            </div>

                            <p class="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 !mt-5">
                                {{ $t('apartments.specifications') }}
                            </p>
                            <!-- Other Topics -->
                            <transition-group enter-active-class="transition ease-out duration-100"
                                enter-from-class="transform opacity-0 scale-95"
                                enter-to-class="transform opacity-100 scale-100"
                                leave-active-class="transition ease-in duration-75"
                                leave-from-class="transform opacity-100 scale-100"
                                leave-to-class="transform opacity-0 scale-95">
                                <Link v-for="item in otherTopics" :key="item.name" :href="makeRoute(item)" :class="[
                                    isCurrent(item)
                                        ? 'bg-gray-200 text-gray-900'
                                        : 'hover:bg-gray-50 hover:text-gray-900',
                                    'group flex items-center px-2 py-2 text-sm rounded-md',
                                ]">
                                <i :class="isCurrent(item)
                                        ? [
                                            'text-gray-500 mr-3 h-6 w-6',
                                            item.icon,
                                        ]
                                        : [
                                            'group-hover:text-gray-500 mr-3 h-6 w-6',
                                            item.icon,
                                        ]
                                    " aria-hidden="true">
                                    <span class="relative text-sm">
                                        <ClockIcon class="absolute -mt-2 -ml-1 h-3 w-3 text-red-400" v-if="item.overdue"
                                            aria-hidden="true" />
                                        <CheckCircleIcon class="absolute -mt-2 -ml-1 h-3 w-3 text-green-400" v-if="
                                            item.state === 'completed' &&
                                            !item.overdue
                                        " aria-hidden="true" />
                                        <PlayIcon class="absolute -mt-2 -ml-1 h-3 w-3 text-yellow-400" v-if="
                                            item.state === 'in_process' &&
                                            !item.overdue
                                        " aria-hidden="true" />
                                        <ExclamationCircleIcon class="absolute -mt-2 -ml-1 h-3 w-3 text-red-400" v-if="
                                            item.state === 'blocked' &&
                                            !item.overdue
                                        " aria-hidden="true" />
                                    </span>
                                </i>
                                {{ item.name }}
                                <span v-if="item.deadlineColor && $page.props.deadline_indicator_show_in_apartment_sidebar_specifications" 
                                    class="ml-auto h-2 w-2 rounded-full shrink-0" 
                                    :style="{ backgroundColor: item.deadlineColor }">
                                </span>
                                </Link>
                            </transition-group>
                        </div>
                    </nav>

                </div>
            </div>
        </div>
    </div>

    <!-- Narrow sidebar -->
    <div class="hidden w-28 overflow-y-auto md:block xl:hidden"
        :style="{ backgroundColor: $page.props.branding?.sidebar_color || '#2f3441', color: $page.props.branding?.sidebar_text_color || '#ffffff' }">
        <div class="w-full flex flex-col items-center">
            <div class="flex-shrink-0 flex items-center py-4">
                <Link href="/">
                <img v-if="$page.props.branding?.logo_url" class="h-8 w-auto" :src="$page.props.branding.logo_url"
                    alt="Agency Logo" />
                <img v-else class="h-8 w-auto" src="../../../image/minplan-icon.svg" alt="immivo" />
                </Link>
            </div>
            <div class="flex-1 w-full px-2 space-y-1">
                <!-- Overview Topic -->
                <Link v-if="overviewTopic" :key="overviewTopic.name" :href="makeRoute(overviewTopic)" :class="[
                    isCurrent(overviewTopic)
                        ? 'bg-gray-200 text-gray-900'
                        : 'hover:bg-gray-50 hover:text-gray-900',
                    'group w-full p-2 rounded-md flex flex-col items-center text-xs',
                ]" :aria-current="isCurrent(overviewTopic) ? 'page' : undefined">
                <i :class="isCurrent(overviewTopic)
                        ? ['text-gray-500 h-6 w-6', overviewTopic.icon]
                        : [
                            'group-hover:text-gray-500 h-6 w-6',
                            overviewTopic.icon,
                        ]
                    " aria-hidden="true">
                    <span class="relative text-sm">
                        <ClockIcon class="absolute -mt-2 -ml-1 h-3 w-3 text-red-400" v-if="overviewTopic.overdue"
                            aria-hidden="true" />
                        <CheckCircleIcon class="absolute -mt-2 -ml-1 h-3 w-3 text-green-400"
                            v-if="overviewTopic.state === 'completed' && !overviewTopic.overdue" aria-hidden="true" />
                        <PlayIcon class="absolute -mt-2 -ml-1 h-3 w-3 text-yellow-400"
                            v-if="overviewTopic.state === 'in_process' && !overviewTopic.overdue" aria-hidden="true" />
                        <ExclamationCircleIcon class="absolute -mt-2 -ml-1 h-3 w-3 text-red-400"
                            v-if="overviewTopic.state === 'blocked' && !overviewTopic.overdue" aria-hidden="true" />
                    </span>
                </i>
                <span class="mt-2 inline-block w-full text-xs truncate text-ellipsis overflow-hidden">{{
                    overviewTopic.name
                    }}</span>
                </Link>

                <!-- Communication section for narrow sidebar -->
                <div v-if="canShowCommunication || canShowNotes"
                    class="pt-4 mt-4 pb-4 mb-4 border-t border-b border-gray-600/20 w-full flex flex-col items-center space-y-1">
                    <Link v-if="canShowNotes" :href="route(notesRouteString, apartment.uuid)" :class="[
                        isNotesActive
                            ? 'bg-gray-200 text-gray-900'
                            : 'hover:bg-gray-50 hover:text-gray-900',
                        'group w-full p-2 rounded-md flex flex-col items-center text-xs',
                    ]">
                    <PencilAltIcon :class="[
                        isNotesActive ? 'text-gray-500' : 'text-gray-400 group-hover:text-gray-500',
                        'h-6 w-6'
                    ]" aria-hidden="true" />
                    <span class="mt-2 inline-block w-full text-xs text-center">{{ $t("apartments.notes") }}</span>
                    </Link>

                    <Link v-if="canShowCommunication" :href="route(chatRouteString, defaultChatRouteParams)" :class="[
                        isChatActive
                            ? 'bg-gray-200 text-gray-900'
                            : 'hover:bg-gray-50 hover:text-gray-900',
                        'group w-full p-2 rounded-md flex flex-col items-center text-xs relative',
                    ]">
                    <ChatIcon :class="[
                        isChatActive ? 'text-gray-500' : 'text-gray-400 group-hover:text-gray-500',
                        'h-6 w-6'
                    ]" aria-hidden="true" />
                    <span class="mt-2 inline-block w-full text-xs text-center">{{ $t('chat.chat') || 'Chat' }}</span>
                    <span v-if="totalUnreadCount > 0"
                        class="absolute top-1 right-1 inline-block py-0.5 px-1.5 text-[10px] font-medium bg-red-500 text-white rounded-full">
                        {{ totalUnreadCount }}
                    </span>
                    </Link>
                </div>

                <transition-group enter-active-class="transition ease-out duration-100"
                    enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100"
                    leave-active-class="transition ease-in duration-75"
                    leave-from-class="transform opacity-100 scale-100" leave-to-class="transform opacity-0 scale-95">
                    <Link v-for="item in otherTopics" :key="item.name" :href="makeRoute(item)" :class="[
                        isCurrent(item)
                            ? 'bg-gray-200 text-gray-900'
                            : 'hover:bg-gray-50 hover:text-gray-900',
                        'group w-full p-2 rounded-md flex flex-col items-center text-xs',
                    ]" :aria-current="isCurrent(item) ? 'page' : undefined">
                    <i :class="isCurrent(item)
                            ? ['text-gray-500 h-6 w-6', item.icon]
                            : [
                                'group-hover:text-gray-500 h-6 w-6',
                                item.icon,
                            ]
                        " aria-hidden="true">
                        <span class="relative text-sm">
                            <ClockIcon class="absolute -mt-2 -ml-1 h-3 w-3 text-red-400" v-if="item.overdue"
                                aria-hidden="true" />
                            <CheckCircleIcon class="absolute -mt-2 -ml-1 h-3 w-3 text-green-400"
                                v-if="item.state === 'completed' && !item.overdue" aria-hidden="true" />
                            <PlayIcon class="absolute -mt-2 -ml-1 h-3 w-3 text-yellow-400"
                                v-if="item.state === 'in_process' && !item.overdue" aria-hidden="true" />
                            <ExclamationCircleIcon class="absolute -mt-2 -ml-1 h-3 w-3 text-red-400"
                                v-if="item.state === 'blocked' && !item.overdue" aria-hidden="true" />
                        </span>
                    </i>
                    <span class="mt-2 flex items-center justify-center w-full min-w-0 text-xs" :title="item.name">
                        <span class="truncate">{{ item.name }}</span>
                        <span v-if="item.deadlineColor && $page.props.deadline_indicator_show_in_apartment_sidebar_specifications" 
                            class="ml-1 h-1.5 w-1.5 rounded-full shrink-0" 
                            :style="{ backgroundColor: item.deadlineColor }">
                        </span>
                    </span>
                    </Link>
                </transition-group>
            </div>
        </div>
    </div>

    <!-- Mobile menu -->
    <TransitionRoot as="template" :show="mobileMenuOpen">
        <Dialog as="div" class="md:hidden" @close="$emit('closeMobileMenu')">
            <div class="fixed inset-0 z-40 flex">
                <TransitionChild as="template" enter="transition-opacity ease-linear duration-300"
                    enter-from="opacity-0" enter-to="opacity-100" leave="transition-opacity ease-linear duration-300"
                    leave-from="opacity-100" leave-to="opacity-0">
                    <DialogOverlay class="fixed inset-0 bg-gray-600/75" />
                </TransitionChild>
                <TransitionChild as="template" enter="transition ease-in-out duration-300 transform"
                    enter-from="-translate-x-full" enter-to="translate-x-0"
                    leave="transition ease-in-out duration-300 transform" leave-from="translate-x-0"
                    leave-to="-translate-x-full">
                    <div class="relative max-w-xs w-full pt-5 pb-4 flex-1 flex flex-col"
                        :style="{ backgroundColor: $page.props.branding?.sidebar_color || '#2f3441', color: $page.props.branding?.sidebar_text_color || '#ffffff' }">
                        <TransitionChild as="template" enter="ease-in-out duration-300" enter-from="opacity-0"
                            enter-to="opacity-100" leave="ease-in-out duration-300" leave-from="opacity-100"
                            leave-to="opacity-0">
                            <div class="absolute top-1 right-0 -mr-14 p-1">
                                <button type="button"
                                    class="h-12 w-12 rounded-full flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-white"
                                    @click="$emit('closeMobileMenu')">
                                    <XIcon class="h-6 w-6 text-white" aria-hidden="true" />
                                    <span class="sr-only">Close sidebar</span>
                                </button>
                            </div>
                        </TransitionChild>
                        <div class="flex-shrink-0 px-4 flex items-center">
                            <Link href="/" @click="$emit('closeMobileMenu')">
                            <img v-if="$page.props.branding?.logo_url" class="h-10 w-auto"
                                :src="$page.props.branding.logo_url" alt="Agency Logo" />
                            <img v-else class="h-10 w-auto" src="../../../image/immivo.svg" alt="immivo" />
                            </Link>
                        </div>
                        <div class="mt-5 flex-1 h-0 px-2 overflow-y-auto">
                            <nav class="h-full flex flex-col">
                                <div class="space-y-1">
                                    <!-- Overview Topic -->
                                    <Link v-if="overviewTopic" :key="overviewTopic.name"
                                        :href="makeRoute(overviewTopic)" @click="$emit('closeMobileMenu')" :class="[
                                            isCurrent(overviewTopic)
                                                ? 'bg-gray-200 text-gray-900'
                                                : 'hover:bg-gray-50 hover:text-gray-900',
                                            'group py-2 px-3 rounded-md flex items-center text-sm',
                                        ]" :aria-current="isCurrent(overviewTopic) ? 'page' : undefined
                                            ">
                                    <i :class="isCurrent(overviewTopic)
                                            ? [
                                                'text-gray-500 mr-3 h-6 w-6',
                                                overviewTopic.icon,
                                            ]
                                            : [
                                                'group-hover:text-gray-900 mr-3 h-6 w-6',
                                                overviewTopic.icon,
                                            ]
                                        " aria-hidden="true">
                                        <span class="relative text-sm">
                                            <ClockIcon class="absolute -mt-2 -ml-1 h-3 w-3 text-red-400"
                                                v-if="overviewTopic.overdue" aria-hidden="true" />
                                            <CheckCircleIcon class="absolute -mt-2 -ml-1 h-3 w-3 text-green-400" v-if="
                                                overviewTopic.state === 'completed' &&
                                                !overviewTopic.overdue
                                            " aria-hidden="true" />
                                            <PlayIcon class="absolute -mt-2 -ml-1 h-3 w-3 text-yellow-400" v-if="
                                                overviewTopic.state === 'in_process' &&
                                                !overviewTopic.overdue
                                            " aria-hidden="true" />
                                            <ExclamationCircleIcon class="absolute -mt-2 -ml-1 h-3 w-3 text-red-400"
                                                v-if="
                                                    overviewTopic.state === 'blocked' &&
                                                    !overviewTopic.overdue
                                                " aria-hidden="true" />
                                        </span>
                                    </i>
                                    <span>{{ overviewTopic.name }}</span>
                                    </Link>

                                    <!-- Mobile Communication Section -->
                                    <div v-if="canShowCommunication || canShowNotes" class="mt-6 mb-6 space-y-1">
                                        <p
                                            class="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                                            {{ $t('apartments.communication') || 'COMMUNICATION' }}
                                        </p>

                                        <Link v-if="canShowNotes" :href="route(notesRouteString, apartment.uuid)"
                                            @click="$emit('closeMobileMenu')" :class="[
                                                isNotesActive
                                                    ? 'bg-gray-200 text-gray-900'
                                                    : 'hover:bg-gray-50 hover:text-gray-900',
                                                'group py-2 px-3 rounded-md flex items-center text-sm',
                                            ]">
                                        <PencilAltIcon :class="[
                                            isNotesActive ? 'text-gray-500' : 'text-gray-400 group-hover:text-gray-500',
                                            'mr-3 h-6 w-6'
                                        ]" aria-hidden="true" />
                                        <span class="flex-1">{{ $t("apartments.notes") }}</span>
                                        </Link>

                                        <Link v-if="canShowCommunication" :href="route(chatRouteString, defaultChatRouteParams)"
                                            @click="$emit('closeMobileMenu')" :class="[
                                                isChatActive
                                                    ? 'bg-gray-200 text-gray-900'
                                                    : 'hover:bg-gray-50 hover:text-gray-900',
                                                'group py-2 px-3 rounded-md flex items-center text-sm',
                                            ]">
                                        <ChatIcon :class="[
                                            isChatActive ? 'text-gray-500' : 'text-gray-400 group-hover:text-gray-500',
                                            'mr-3 h-6 w-6'
                                        ]" aria-hidden="true" />
                                        <span class="flex-1">Chat</span>
                                        <span v-if="totalUnreadCount > 0"
                                            class="ml-3 inline-block py-0.5 px-3 text-xs font-medium bg-red-500 text-white rounded-full">
                                            {{ totalUnreadCount }}
                                        </span>
                                        </Link>
                                    </div>

                                    <p
                                        class="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 !mt-5">
                                        {{ $t('apartments.specifications') || 'SPECIFICATIONS' }}
                                    </p>

                                    <!-- Other Topics -->
                                    <Link v-for="item in otherTopics" :key="item.name" :href="makeRoute(item)"
                                        @click="$emit('closeMobileMenu')" :class="[
                                            isCurrent(item)
                                                ? 'bg-gray-200 text-gray-900'
                                                : 'hover:bg-gray-50 hover:text-gray-900',
                                            'group py-2 px-3 rounded-md flex items-center text-sm',
                                        ]" :aria-current="isCurrent(item) ? 'page' : undefined
                                            ">
                                    <i :class="isCurrent(item)
                                            ? [
                                                'text-gray-500 mr-3 h-6 w-6',
                                                item.icon,
                                            ]
                                            : [
                                                'group-hover:text-gray-900 mr-3 h-6 w-6',
                                                item.icon,
                                            ]
                                        " aria-hidden="true">
                                        <span class="relative text-sm">
                                            <ClockIcon class="absolute -mt-2 -ml-1 h-3 w-3 text-red-400"
                                                v-if="item.overdue" aria-hidden="true" />
                                            <CheckCircleIcon class="absolute -mt-2 -ml-1 h-3 w-3 text-green-400" v-if="
                                                item.state === 'completed' &&
                                                !item.overdue
                                            " aria-hidden="true" />
                                            <PlayIcon class="absolute -mt-2 -ml-1 h-3 w-3 text-yellow-400" v-if="
                                                item.state === 'in_process' &&
                                                !item.overdue
                                            " aria-hidden="true" />
                                            <ExclamationCircleIcon class="absolute -mt-2 -ml-1 h-3 w-3 text-red-400"
                                                v-if="
                                                    item.state === 'blocked' &&
                                                    !item.overdue
                                                " aria-hidden="true" />
                                        </span>
                                    </i>
                                    <span>{{ item.name }}</span>
                                    <span v-if="item.deadlineColor && $page.props.deadline_indicator_show_in_apartment_sidebar_specifications" 
                                        class="ml-auto h-2 w-2 rounded-full shrink-0" 
                                        :style="{ backgroundColor: item.deadlineColor }">
                                    </span>
                                    </Link>
                                </div>
                            </nav>
                        </div>
                    </div>
                </TransitionChild>
                <div class="flex-shrink-0 w-14" aria-hidden="true">
                    <!-- Dummy element to force sidebar to shrink to fit close icon -->
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script>
import { defineComponent } from "vue";
import { Link } from "@inertiajs/inertia-vue3";
import { encodeBuyerId, decodeBuyerId } from "@/Shared/Utils/buyerIdObfuscation";
import {
    CheckCircleIcon,
    ClockIcon,
    ExclamationCircleIcon,
    PlayIcon,
    XIcon,
    ChatIcon,
    PencilAltIcon,
} from "@heroicons/vue/outline";
import { Dialog, DialogOverlay, TransitionChild, TransitionRoot } from "@headlessui/vue";

export default defineComponent({
    props: [
        "topics",
        "topic",
        "mobileMenuOpen",
        "apartment",
        "buyers",
        "showApartmentRouteString",
        "showTopicRouteString",
    ],
    emits: ["closeMobileMenu"],
    components: {
        Link,
        ChatIcon,
        PencilAltIcon,
        ExclamationCircleIcon,
        PlayIcon,
        CheckCircleIcon,
        ClockIcon,
        TransitionChild,
        TransitionRoot,
        DialogOverlay,
        Dialog,
        XIcon,
    },
    data() {
        return {};
    },
    created() {},
    watch: {},
    methods: {
        getBuyerUnreadCount(index) {
            const buyer = this.chatBuyers?.[index];
            return Number(buyer?.unread_count || 0);
        },
        buildDefaultChatRouteParams() {
            return this.apartment.uuid;
        },
        isCurrent(topic) {
            if (this.isChatActive) return false;
            return this.topic ? this.topic.uuid === topic.uuid : topic.uuid === null;
        },
        makeRoute(topic) {
            if (!topic.uuid) {
                return this.route(this.showApartmentRouteString, this.apartment.uuid);
            }
            return this.route(this.showTopicRouteString, {
                apartment: this.apartment.uuid,
                topic: topic.uuid,
            });
        },
        encodeId(id) {
            return encodeBuyerId(id);
        },
        decodeId(encoded) {
            return decodeBuyerId(encoded);
        }
    },
    computed: {
        overviewTopic() {
            return (this.topics || []).find(t => !t.uuid);
        },
        otherTopics() {
            return (this.topics || []).filter(t => t.uuid);
        },
        // Betreuer sehen den Punkt immer (Thema 7). Käufer nur, wenn der
        // Server ihn freigeschaltet hat — Auftrag Luca, 2026-08-17,
        // Entscheid 7: ohne eine einzige freigegebene Notiz sieht die
        // Käuferansicht aus wie vorher, also auch ohne diesen Menüpunkt.
        //
        // Das Signal ist `notesLink`. Es liegt beim Käufer NUR an, wenn die
        // Wohnung mindestens eine freigegebene Notiz hat, und wird von DREI
        // Resources geliefert: `ApartmentShowResource` (Übersicht, Chat,
        // Notizen-Seite), `TopicShowResource` (Themenseite) und
        // `OrderModificationResource` (Detailseite einer
        // Bestellungsänderung). Diese Navigation rendert auf jeder dieser
        // Seiten; käme das Signal nur aus der Wohnungs-Resource, fehlte der
        // Punkt auf den übrigen — und zwar lautlos.
        //
        // Reine Lese-Nutzung der Seiten-Props über `$page`, wie
        // `canShowCommunication()` es mit `$page.props.user` schon tut;
        // HandleInertiaRequests bleibt unberührt (Kontrollpunkt).
        canShowNotes() {
            if (this.$page.props.user?.type === "advisor") {
                return true;
            }

            return Boolean(this.$page.props.notesLink);
        },
        notesRouteString() {
            return this.showApartmentRouteString.replace(".show", ".notes");
        },
        isNotesActive() {
            return this.route().current(this.notesRouteString);
        },
        canShowCommunication() {
            if (!this.chatBuyers || this.chatBuyers.length === 0) {
                return false;
            }

            if (this.$page.props.user.type === "buyer") {
                const isDisabled = this.chatBuyers.some(b => b.chat_is_disabled);
                return !isDisabled;
            }

            const role = this.$page.props.user.currentAgencyRole;
            if (role === "owner" || role === "manager" || role === "advisor") {
                return true;
            }

            return false;
        },
        chatBuyers() {
            const buyers = Array.isArray(this.buyers) && this.buyers.length
                ? this.buyers
                : (this.apartment?.buyers || []);

            const role = this.$page.props.user.currentAgencyRole;
            if (['owner', 'manager', 'advisor'].includes(role)) {
                return buyers;
            }

            return buyers.filter((buyer) => Number(buyer.user_id) === Number(this.$page.props.user.id));
        },

        chatRouteString() {
            return this.showApartmentRouteString.replace('.show', '.chat');
        },
        isChatActive() {
            return this.route().current(this.chatRouteString);
        },
        totalUnreadCount() {
            if (!this.chatBuyers.length) return 0;
            
            const seenThreads = new Set();
            return this.chatBuyers.reduce((sum, buyer) => {
                if (buyer.chat_thread_id && !seenThreads.has(buyer.chat_thread_id)) {
                    seenThreads.add(buyer.chat_thread_id);
                    return sum + Number(buyer.unread_count || 0);
                }
                // Fallback for cases where chat_thread_id might be missing but we have an unread count
                if (!buyer.chat_thread_id && !seenThreads.has('default')) {
                    seenThreads.add('default');
                    return sum + Number(buyer.unread_count || 0);
                }
                return sum;
            }, 0);
        },
        defaultChatRouteParams() {
            return this.buildDefaultChatRouteParams();
        },
    },
});
</script>

<style scoped></style>
